import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  CommandeFournisseurRecord,
  CommandeFournisseurStatut,
  commandeFournisseurApiService,
} from '../services/CommandeFournisseurApiService';
import { exportCommandeService } from '../services/ExportCommandeService';
import { CommandeGroupee } from '../services/CommandeFournisseurService';

const statutBadge = (statut: CommandeFournisseurStatut) => {
  switch (statut) {
    case 'BROUILLON':
      return 'bg-blue-100 text-blue-800';
    case 'ENVOYEE':
      return 'bg-yellow-100 text-yellow-800';
    case 'RECUE':
      return 'bg-green-100 text-green-800';
    case 'ANNULEE':
      return 'bg-red-100 text-red-800';
    case 'GENEREE':
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const formatDate = (value?: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatMontant = (montant?: number | null) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(montant || 0);

const safeArray = <T,>(value: any): T[] => (Array.isArray(value) ? value : []);

type DevisBadge = { reference: string; id?: string };

const CommandeFournisseurDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [commande, setCommande] = useState<CommandeFournisseurRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statutUpdating, setStatutUpdating] = useState(false);

  const fetchCommande = async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const data = await commandeFournisseurApiService.lireCommande(id);
      setCommande(data);
    } catch (err: any) {
      console.error('Erreur lecture commande fournisseur:', err);
      setError(err?.message || 'Commande introuvable.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchCommande();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleMajStatut = async (nouveauStatut: CommandeFournisseurStatut) => {
    if (!id) return;
    try {
      setStatutUpdating(true);
      const updated = await commandeFournisseurApiService.majStatut(id, nouveauStatut);
      setCommande((prev) => (prev ? { ...prev, ...updated, statut: updated.statut } : prev));
    } catch (err: any) {
      console.error('Erreur mise a jour statut commande fournisseur:', err);
      alert(err?.message || 'Impossible de mettre a jour le statut.');
    } finally {
      setStatutUpdating(false);
    }
  };

  const handleExport = () => {
    if (!commande) return;
    const payload = commande.payload || {};
    const matieres = safeArray<any>(payload.matieres);
    const fournitures = safeArray<any>(payload.fournitures);

    let csv = '\uFEFF';
    csv += 'Numero;Statut;Date creation;Date maj;Montant;Devis\n';
    const montant = commande.totaux?.montantTotal ?? commande.total_ht ?? 0;
    csv += `"${commande.numeroCommande}";"${commande.statut}";"${formatDate(
      commande.dateCreation || commande.created_at || undefined,
    )}";"${formatDate(commande.updated_at)}";${montant};"${(commande.devisInclus || []).join(', ')}"\n`;

    if (matieres.length > 0) {
      csv += '\nMatieres;\nNom;Quantite;PU HT;Montant HT;Devis\n';
      matieres.forEach((matiere: any) => {
        const devis = safeArray<any>(matiere.devisOrigines || matiere.devis_origines).map(
          (d) => d.reference || d.devisReference || '',
        );
        csv += `"${matiere.nom || ''}";${matiere.quantiteMetres ?? ''};${matiere.prixUnitaire ?? ''};${
          matiere.montantTotal ?? ''
        };"${devis.join(', ')}"\n`;
      });
    }

    if (fournitures.length > 0) {
      csv += '\nFournitures;\nNom;Quantite;PU HT;Montant HT;Devis\n';
      fournitures.forEach((fourniture: any) => {
        const devis = safeArray<any>(fourniture.devisOrigines || fourniture.devis_origines).map(
          (d) => d.reference || d.devisReference || '',
        );
        csv += `"${fourniture.nom || ''}";${fourniture.quantite ?? ''};${fourniture.prixUnitaire ?? ''};${
          fourniture.montantTotal ?? ''
        };"${devis.join(', ')}"\n`;
      });
    }

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `commande_fournisseur_${commande.numeroCommande || commande.id}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const buildCommandeForExport = (): CommandeGroupee | null => {
    if (!commande) return null;
    const payload = commande.payload || {};
    const matieres = safeArray<any>(payload.matieres);
    const fournitures = safeArray<any>(payload.fournitures);
    const totaux = payload.totaux || commande.totaux || {};
    const devisList = safeArray<string>(payload.devisInclus || commande.devisInclus);

    return {
      numeroCommande: commande.numeroCommande || commande.id,
      dateCreation:
        payload.dateCreation ||
        commande.dateCreation ||
        commande.created_at ||
        new Date().toISOString(),
      devisInclus: devisList,
      matieres,
      fournitures,
      totaux: {
        montantMatieres: Number(totaux.montantMatieres || 0),
        montantFournitures: Number(totaux.montantFournitures || 0),
        montantTotal: Number(totaux.montantTotal || commande.total_ht || 0),
        nombreDevis:
          Number(totaux.nombreDevis) || devisList.length || matieres.length || fournitures.length,
      },
      statistiques: payload.statistiques || { matieresExcluesStock: 0, matieresACommander: 0 },
    };
  };

  const handleExportPdf = async () => {
    try {
      const commandeExport = buildCommandeForExport();
      if (!commandeExport) return;
      await exportCommandeService.exportToPDF(commandeExport);
    } catch (err: any) {
      console.error('Erreur export PDF commande fournisseur:', err);
      alert(err?.message || 'Impossible de generer le PDF.');
    }
  };

  const payload = commande?.payload || {};
  const matieres = safeArray<any>(payload.matieres);
  const fournitures = safeArray<any>(payload.fournitures);
  const totaux = payload.totaux || commande?.totaux;
  const totalMatieres =
    typeof totaux?.montantMatieres === 'number' ? totaux?.montantMatieres : undefined;
  const totalFournitures =
    typeof totaux?.montantFournitures === 'number' ? totaux?.montantFournitures : undefined;
  const totalGeneral = totaux?.montantTotal ?? commande?.total_ht ?? 0;

  const devisAssocies = useMemo<DevisBadge[]>(() => {
    const seen = new Set<string>();
    const list: DevisBadge[] = [];
    const addDevis = (reference?: string, devisId?: string) => {
      const key = reference || devisId;
      if (!key) return;
      if (seen.has(key)) return;
      seen.add(key);
      list.push({ reference: reference || devisId || '', id: devisId || undefined });
    };

    matieres.forEach((matiere: any) => {
      safeArray<any>(matiere.devisOrigines || matiere.devis_origines).forEach((d) =>
        addDevis(d.reference || d.devisReference, d.devisId || d.devis_id),
      );
    });
    fournitures.forEach((fourniture: any) => {
      safeArray<any>(fourniture.devisOrigines || fourniture.devis_origines).forEach((d) =>
        addDevis(d.reference || d.devisReference, d.devisId || d.devis_id),
      );
    });
    (commande?.devisInclus || []).forEach((ref) => addDevis(ref));
    return list;
  }, [commande?.devisInclus, fournitures, matieres]);

  if (loading) {
    return <div className="p-8 text-center text-gray-500">Chargement de la commande...</div>;
  }

  if (error || !commande) {
    return (
      <div className="space-y-4">
        <div className="p-8 text-center text-red-600">{error || 'Commande introuvable.'}</div>
        <button
          type="button"
          onClick={() => navigate('/commandes-fournisseur')}
          className="text-primary-600 hover:text-primary-800 underline"
        >
          Retour a la liste
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg p-4 md:p-6 space-y-4">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="space-y-1">
            <h1 className="text-2xl font-semibold text-gray-900">
              Commande fournisseur {commande.numeroCommande || commande.id}
            </h1>
            <p className="text-sm text-gray-500">
              Lecture seule ; mise a jour du statut et export CSV disponibles.
            </p>
            <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
              <span className="flex items-center gap-1">
                <span className="text-gray-500">Creee</span>
                <span className="font-medium">
                  {formatDate(commande.dateCreation || commande.created_at || undefined)}
                </span>
              </span>
              <span className="hidden sm:inline-block h-4 w-px bg-gray-200" />
              <span className="flex items-center gap-1">
                <span className="text-gray-500">Mise a jour</span>
                <span className="font-medium">{formatDate(commande.updated_at)}</span>
              </span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${statutBadge(
                commande.statut,
              )}`}
            >
              {commande.statut}
            </span>
            <select
              value={commande.statut}
              onChange={(e) => handleMajStatut(e.target.value as CommandeFournisseurStatut)}
              className="text-xs border border-gray-300 rounded px-2 py-1"
              disabled={statutUpdating}
            >
              <option value="BROUILLON">Brouillon</option>
              <option value="ENVOYEE">Envoyee</option>
              <option value="RECUE">Recue</option>
              <option value="ANNULEE">Annulee</option>
            </select>
            <button
              type="button"
              onClick={handleExport}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 transition-colors duration-200"
            >
              Exporter CSV
            </button>
            <button
              type="button"
              onClick={handleExportPdf}
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200"
            >
              Exporter PDF
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-medium text-gray-900">Devis associes</h2>
          {devisAssocies.length > 0 && (
            <span className="text-xs text-gray-500">
              {devisAssocies.length} devis{devisAssocies.length > 1 ? 's' : ''} lie
              {devisAssocies.length > 1 ? 's' : ''}
            </span>
          )}
        </div>
        {devisAssocies.length === 0 ? (
          <div className="text-sm text-gray-500">Aucun devis associe.</div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {devisAssocies.slice(0, 3).map((devis) =>
              devis.id ? (
                <Link
                  key={`${devis.reference}-${devis.id}`}
                  to={`/devis/configurateur/${devis.id}`}
                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 hover:bg-blue-200"
                >
                  {devis.reference}
                </Link>
              ) : (
                <span
                  key={devis.reference}
                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                >
                  {devis.reference}
                </span>
              ),
            )}
            {devisAssocies.length > 3 && (
              <span className="text-xs text-gray-500">
                +{devisAssocies.length - 3} autre{devisAssocies.length - 3 > 1 ? 's' : ''}
              </span>
            )}
          </div>
        )}
      </div>

      <div className="bg-white shadow rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-medium text-gray-900">Matieres</h2>
          {typeof totalMatieres === 'number' && (
            <span className="text-sm font-medium text-gray-700">{formatMontant(totalMatieres)}</span>
          )}
        </div>
        {matieres.length === 0 ? (
          <div className="text-sm text-gray-500">Aucune matiere dans cette commande.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Nom
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Quantite
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    PU HT
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Montant HT
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Devis d'origine
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {matieres.map((matiere: any, index: number) => {
                  const devis = safeArray<any>(matiere.devisOrigines || matiere.devis_origines)
                    .map((d) => d.reference || d.devisReference)
                    .filter(Boolean)
                    .join(', ');
                  return (
                    <tr key={`${matiere.nom || 'matiere'}-${index}`} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-sm text-gray-900">{matiere.nom || '-'}</td>
                      <td className="px-4 py-2 text-sm text-gray-600">
                        {typeof matiere.quantiteMetres === 'number'
                          ? `${matiere.quantiteMetres.toFixed(2)} m`
                          : '-'}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-600">
                        {typeof matiere.prixUnitaire === 'number'
                          ? `${formatMontant(matiere.prixUnitaire)} / m`
                          : '-'}
                      </td>
                      <td className="px-4 py-2 text-sm font-medium text-gray-900">
                        {typeof matiere.montantTotal === 'number'
                          ? formatMontant(matiere.montantTotal)
                          : '-'}
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-500">{devis || '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white shadow rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-medium text-gray-900">Fournitures</h2>
          {typeof totalFournitures === 'number' && (
            <span className="text-sm font-medium text-gray-700">{formatMontant(totalFournitures)}</span>
          )}
        </div>
        {fournitures.length === 0 ? (
          <div className="text-sm text-gray-500">Aucune fourniture dans cette commande.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Nom
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Quantite
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    PU HT
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Montant HT
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Devis d'origine
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {fournitures.map((fourniture: any, index: number) => {
                  const devis = safeArray<any>(fourniture.devisOrigines || fourniture.devis_origines)
                    .map((d) => d.reference || d.devisReference)
                    .filter(Boolean)
                    .join(', ');
                  const unite = fourniture.unite || '';
                  return (
                    <tr key={`${fourniture.nom || 'fourniture'}-${index}`} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-sm text-gray-900">{fourniture.nom || '-'}</td>
                      <td className="px-4 py-2 text-sm text-gray-600">
                        {typeof fourniture.quantite === 'number'
                          ? `${fourniture.quantite.toFixed(2)} ${unite}`.trim()
                          : '-'}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-600">
                        {typeof fourniture.prixUnitaire === 'number'
                          ? `${formatMontant(fourniture.prixUnitaire)}${unite ? ` / ${unite}` : ''}`
                          : '-'}
                      </td>
                      <td className="px-4 py-2 text-sm font-medium text-gray-900">
                        {typeof fourniture.montantTotal === 'number'
                          ? formatMontant(fourniture.montantTotal)
                          : '-'}
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-500">{devis || '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white shadow rounded-lg p-4 space-y-2 lg:col-span-2">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-medium text-gray-900">Totaux</h2>
            <span className="text-sm text-gray-500">HT</span>
          </div>
          <div className="text-sm text-gray-700 space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-500">Total matieres</span>
              <span className="font-medium">
                {typeof totalMatieres === 'number' ? formatMontant(totalMatieres) : '-'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Total fournitures</span>
              <span className="font-medium">
                {typeof totalFournitures === 'number' ? formatMontant(totalFournitures) : '-'}
              </span>
            </div>
            <div className="flex justify-between border-t border-gray-200 pt-2">
              <span className="text-gray-600 font-semibold">Total general</span>
              <span className="font-semibold text-primary-700">{formatMontant(totalGeneral)}</span>
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-4 space-y-2">
          <h2 className="text-lg font-medium text-gray-900">Notes / meta</h2>
          {payload.notes || payload.note ? (
            <p className="text-sm text-gray-700 whitespace-pre-line">
              {payload.notes || payload.note}
            </p>
          ) : payload ? (
            <p className="text-sm text-gray-500">Aucune note enregistree pour cette commande.</p>
          ) : (
            <p className="text-sm text-gray-500">Payload indisponible.</p>
          )}
          {payload.statistiques && (
            <div className="text-xs text-gray-500 space-y-1 border-t border-gray-100 pt-2">
              <div>
                Matieres exclues (stock) :{' '}
                <span className="font-medium text-gray-700">
                  {payload.statistiques.matieresExcluesStock ?? '-'}
                </span>
              </div>
              <div>
                Matieres a commander :{' '}
                <span className="font-medium text-gray-700">
                  {payload.statistiques.matieresACommander ?? '-'}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="text-sm text-gray-500 flex items-center gap-3">
        <Link to="/commandes-fournisseur" className="text-primary-600 hover:text-primary-800 underline">
          Retour a la liste
        </Link>
        <button type="button" onClick={() => navigate(-1)} className="text-gray-600 hover:text-gray-800">
          Revenir en arriere
        </button>
      </div>
    </div>
  );
};

export default CommandeFournisseurDetail;
